import {
  analysisCategorySchema,
  type AnalysisCategory,
  type AnalysisGrade,
  type AnalysisSeverity,
  type AppAnalysisHealth,
} from "@repo/shared";

/** How many health categories the scoring model defines. */
export const ANALYSIS_CATEGORY_COUNT = analysisCategorySchema.options.length;

/**
 * Whether a health payload actually scored a category. Everything is scored
 * except object-complexity on a phase-1 payload (`version` < 2), whose 100 is a
 * placeholder that was left out of the overall average.
 */
export function isCategoryScored(
  health: Pick<AppAnalysisHealth, "version"> | null | undefined,
  categoryId: AnalysisCategory,
): boolean {
  return categoryId !== "object-complexity" || (health?.version ?? 1) >= 2;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

// --- Compare (diff) deltas ---
//
// A delta is always shown with an explicit sign and a real minus sign (−), so a
// reduction never reads like a hyphenated label. Zero stays unsigned.

export function formatSignedBytes(delta: number): string {
  if (delta === 0) return "0 B";
  return `${delta > 0 ? "+" : "−"}${formatBytes(Math.abs(delta))}`;
}

export function formatSignedCount(delta: number, locale?: string): string {
  if (delta === 0) return "0";
  return `${delta > 0 ? "+" : "−"}${Math.abs(delta).toLocaleString(locale)}`;
}

export function formatSignedPct(delta: number): string {
  if (delta === 0) return "0%";
  return `${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)}%`;
}

/** Which way a metric has to move for the change to count as an improvement. */
export type DeltaDirection = "lower-is-better" | "higher-is-better" | "neutral";

/** Green when the metric improved, red when it regressed, neutral otherwise. */
export function deltaToneClass(delta: number, direction: DeltaDirection): string {
  if (delta === 0) return "text-dim";
  if (direction === "neutral") return "text-ink";
  const improved = direction === "lower-is-better" ? delta < 0 : delta > 0;
  return improved ? "text-status-success-foreground" : "text-status-danger-foreground";
}

// QVD storage-analysis coverage %. A QVD only counts as "covered" once its
// (opt-in) sizing is also done, so analyzed QVDs still awaiting sizing
// (sizePendingCount) are subtracted from the numerator — the headline does not
// read 100% while the wasted-bytes measurement is pending/interrupted. QVDs whose
// verdict was withheld (evidenceIncompleteCount) are also excluded: the worker
// counts them as processed internally, but the UI must not call them covered.
// Returns null when nothing was inventoried.
export function qvdCoveragePct(coverage: {
  qvdsAnalyzed: number;
  qvdsInventoried: number;
  sizePendingCount?: number;
  evidenceIncompleteCount?: number;
}): number | null {
  if (coverage.qvdsInventoried === 0) return null;
  const covered = Math.max(
    0,
    coverage.qvdsAnalyzed -
      (coverage.sizePendingCount ?? 0) -
      (coverage.evidenceIncompleteCount ?? 0),
  );
  return Math.round((covered / coverage.qvdsInventoried) * 100);
}

export type GradeColorTokens = {
  /** Solid foreground/text colour token. */
  text: string;
  /** Background tint colour token. */
  bg: string;
  /** Border colour token (subtle, for badge outlines). */
  border: string;
  /** Solid pill foreground (white-on-colour for filled badges). */
  solidBg: string;
  solidText: string;
  /** Small dot/indicator background colour. */
  dot: string;
};

const GRADE_COLOR_MAP: Record<AnalysisGrade, GradeColorTokens> = {
  excellent: {
    text: "text-status-success-foreground",
    bg: "bg-status-success-bg",
    border: "border-status-success/30",
    solidBg: "bg-status-success",
    solidText: "text-white",
    dot: "bg-status-success",
  },
  good: {
    text: "text-status-success-foreground",
    bg: "bg-status-success-bg",
    border: "border-status-success/30",
    solidBg: "bg-status-success",
    solidText: "text-white",
    dot: "bg-status-success",
  },
  fair: {
    text: "text-status-warning-foreground",
    bg: "bg-status-warning-bg",
    border: "border-status-warning/30",
    solidBg: "bg-status-warning",
    solidText: "text-white",
    dot: "bg-status-warning",
  },
  poor: {
    text: "text-status-danger-foreground",
    bg: "bg-status-danger-bg",
    border: "border-status-danger/30",
    solidBg: "bg-status-danger",
    solidText: "text-white",
    dot: "bg-status-danger",
  },
  critical: {
    text: "text-status-danger-foreground",
    bg: "bg-status-danger-bg",
    border: "border-status-danger/30",
    solidBg: "bg-status-danger",
    solidText: "text-white",
    dot: "bg-status-danger",
  },
};

export function getGradeColors(grade: AnalysisGrade): GradeColorTokens {
  return GRADE_COLOR_MAP[grade];
}

/** The badge tone of a health grade: excellent and good are green, fair amber, poor and critical red. */
export function gradeTone(grade: AnalysisGrade): "success" | "warning" | "danger" {
  if (grade === "excellent" || grade === "good") return "success";
  return grade === "fair" ? "warning" : "danger";
}

/** The badge tone of a finding's severity: critical is red, high and warning amber, info blue. */
export function severityTone(severity: AnalysisSeverity): "danger" | "warning" | "info" {
  if (severity === "critical") return "danger";
  return severity === "info" ? "info" : "warning";
}

export type SeverityColorTokens = {
  text: string;
  bg: string;
  border: string;
  /** A small dot/indicator background colour. */
  dot: string;
};

const SEVERITY_COLOR_MAP: Record<AnalysisSeverity, SeverityColorTokens> = {
  critical: {
    text: "text-status-danger-foreground",
    bg: "bg-status-danger-bg",
    border: "border-status-danger/40",
    dot: "bg-status-danger",
  },
  high: {
    text: "text-status-warning-foreground",
    bg: "bg-status-warning-bg",
    border: "border-status-warning/40",
    dot: "bg-status-warning",
  },
  warning: {
    text: "text-status-warning-foreground",
    bg: "bg-status-warning-bg",
    border: "border-status-warning/40",
    dot: "bg-status-warning",
  },
  info: {
    text: "text-status-info-foreground",
    bg: "bg-status-info-bg",
    border: "border-status-info/40",
    dot: "bg-status-info",
  },
};

export function getSeverityColors(severity: AnalysisSeverity): SeverityColorTokens {
  return SEVERITY_COLOR_MAP[severity];
}

const SEVERITY_RANK: Record<AnalysisSeverity, number> = {
  critical: 4,
  high: 3,
  warning: 2,
  info: 1,
};

export function severityRank(severity: AnalysisSeverity): number {
  return SEVERITY_RANK[severity];
}
